import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  InfrastructureCreateSchema,
  InfrastructureQueryAllSchema,
  InfrastructureQuerySchema,
  InfrastructureUpdateSchema,
} from './schemas';

export function useGetInfrastructures(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['INFRASTRUCTURES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<InfrastructureQueryAllSchema>({
        endpoint: '/api/v1/infrastructures',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['INFRASTRUCTURES'] });
};

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, InfrastructureCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/infrastructures/',
        payload,
      }),
  });

export const useGetInfrastructuresMutation: UseControlledMutation<InfrastructureQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['INFRASTRUCTURES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<InfrastructureQueryAllSchema>({
            endpoint: '/api/v1/infrastructures',
            params: { search, per_page },
          });

          return orderBy(res?.data ?? [], ['name']) || [];
        },
      });
    },
  });

export function useGetInfrastructure(id: string) {
  return useSuspenseQuery({
    queryKey: ['INFRASTRUCTURE', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: InfrastructureQuerySchema }>({
        endpoint: `/api/v1/infrastructures/${id}`,
        // params: { include: 'location' },
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: InfrastructureQuerySchema }, ApiError, Partial<InfrastructureUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/infrastructures/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['INFRASTRUCTURE_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/infrastructures/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
